import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  InspectionStatusPill,
  INSPECTION_STATUS_ORDER,
} from "../../../components/inspections/InspectionStatusPill";
import { useYardOperationMetrics } from "../../../features/truckYard/useYardMetrics";
import {
  formatAge,
  formatDwell,
  formatLitres,
  readWaterQuality,
  visitsToStatusCounts,
  yardAggregateSum,
  yardBarsTotal,
  yardDistinctUnitsUniverse,
  yardDoorsBreakdown,
  yardDwellSample,
  yardGroupBars,
  yardLinkClasses,
  yardLinkState,
  yardCustomRange,
  yardLitresVisitSample,
  yardPresetRange,
  yardTopWaterType,
  yardUnitBars,
  waterTypeQuality,
  type YardBarDatum,
  type YardBarKind,
  type YardMetricsResponse,
  type YardRangePreset,
} from "./yardOperation";
import type { ThemeClasses, Yard } from "./types";
import YardDownCauseLine from "./YardDownCauseLine";

// ── Recharts y no chart.js ───────────────────────────────────────────────────
//
// `apps/v5` tiene las dos librerías instaladas (package.json:37-38 y :59) y la
// elección no es de gusto: chart.js domina en `pages/reports`, pero ahí se usa
// imperativamente (`new ChartJS(...)`, V5Dashboard.tsx:193,253) y el ciclo de
// vida del canvas queda a cargo del componente — destruir la instancia anterior
// en cada cambio de datos, y este panel cambia de datos cada 30 s y cada vez que
// alguien toca el selector de rango. El ejemplo más cercano a lo que hace este
// archivo —un tablero de métricas con filtros y tema oscuro— ya está resuelto
// con recharts en Layouts/service-desk/sections/DashboardSection.tsx:5-15.

/**
 * Los colores de las barras, que NO son decorativos.
 *
 * Se escriben como hex y no como clases de Tailwind porque recharts pinta un
 * `<rect fill>` en SVG y necesita el valor, no el nombre de la clase.
 *
 *  - `no_group` en violeta: "Sin grupo" es una CATEGORÍA que #501 pide visible,
 *    no un residuo. En gris se leería como el cubo de sobras y es justo lo
 *    contrario — hoy es el destino de toda unidad que no esté en el join
 *    `groups.devices_ids` → `devices.vehicle_id`.
 *  - `missing_vehicle` en ámbar, el mismo hue que ya significa "esto necesita a
 *    alguien" en el eje de enlace: una unidad que sólo se conoce por número de
 *    tarjeta es exactamente una visita en estado `missing_vehicle`, y son 10 de
 *    las 149 inspecciones de producción medidas el 3-sep-2026.
 *  - `others` sí es gris: es el único cubo que de verdad es un residuo, y es el
 *    único del que no se puede decir cuántas categorías agrupa.
 */
const BAR_COLORS: Record<YardBarKind, { dark: string; light: string }> = {
  normal: { dark: "#3B82F6", light: "#2563EB" },
  no_group: { dark: "#A78BFA", light: "#7C3AED" },
  missing_vehicle: { dark: "#FBBF24", light: "#D97706" },
  others: { dark: "#4B5563", light: "#9CA3AF" },
};

/**
 * Una etiqueta de eje no puede empujar el gráfico hasta dejarlo sin barras.
 *
 * 18 caracteres es donde se corta con `width={112}` a 11 px, que es el ancho que
 * deja respirar la barra en el ancho de tablet (la modal es `max-w-6xl` y en dos
 * columnas cada gráfica queda cerca de 380 px). El nombre completo sigue estando
 * en el tooltip, que usa el valor crudo de la categoría.
 */
const truncateTick = (value: string) =>
  value.length > 18 ? `${value.slice(0, 17)}…` : value;

function HorizontalBars({
  data,
  isDarkMode,
  seriesName,
}: {
  data: YardBarDatum[];
  isDarkMode: boolean;
  seriesName: string;
}) {
  // Alto proporcional a la cantidad de barras y no fijo: con 11 categorías (top
  // 10 + "Otros") en un alto fijo de 260 px las barras salen de 12 px y el
  // gráfico se vuelve ilegible; con una sola categoría, 260 px de alto para una
  // barra es una franja vacía. 30 px por barra es lo que deja la etiqueta de
  // 11 px con aire.
  const height = Math.max(140, data.length * 30 + 30);
  const axisTick = { fontSize: 11, fill: isDarkMode ? "#9CA3AF" : "#6B7280" };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
      >
        <CartesianGrid
          horizontal={false}
          stroke={isDarkMode ? "#374151" : "#E5E7EB"}
        />
        <XAxis type="number" allowDecimals={false} tick={axisTick} />
        <YAxis
          type="category"
          dataKey="label"
          width={112}
          tick={axisTick}
          tickFormatter={truncateTick}
        />
        <Tooltip
          cursor={{ fill: isDarkMode ? "#ffffff10" : "#00000008" }}
          contentStyle={{
            backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
            border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`,
            borderRadius: 8,
            fontSize: 12,
            color: isDarkMode ? "#F9FAFB" : "#111827",
          }}
        />
        <Bar dataKey="n" name={seriesName} radius={[0, 4, 4, 0]} maxBarSize={22}>
          {data.map((d) => (
            <Cell
              key={d.key}
              fill={BAR_COLORS[d.kind][isDarkMode ? "dark" : "light"]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Sobre cuántas visitas se repartió la gráfica.
 *
 * Las dos gráficas y la tira de píldoras de arriba NO cuentan lo mismo: las
 * píldoras suman `visits.total`, que incluye las canceladas, y las barras se
 * reparten sólo las NO canceladas —el `continue` de proveedor está antes de los
 * acumuladores de grupo y de unidad (yardMetricsService.js)—. Sin esta línea,
 * "12 visitas en total" arriba y barras que suman 9 abajo no tienen explicación
 * en pantalla.
 *
 * El número sale de `yardBarsTotal(bars)` y no de `water_types.n`: se suma sobre
 * lo que se dibujó, así que la leyenda no puede describir otro universo.
 */
function ChartUniverse({
  bars,
  themeClasses,
}: {
  bars: YardBarDatum[];
  themeClasses: ThemeClasses;
}) {
  const { t } = useTranslation("truckYard");
  return (
    <p className={`text-[11px] ${themeClasses.textSecondary} mt-1`}>
      {t("yards.operation.charts.universe", { universe: yardBarsTotal(bars) })}
    </p>
  );
}

/**
 * Un número con su denominador debajo. Nunca se usa sin `sample`.
 *
 * La regla es de #501 y es la que hace útil al panel entero: cada promedio
 * declara su `n` y sobre qué universo se calculó. El componente no acepta
 * "mostrar sólo el número" a propósito — si un dato no tiene denominador que
 * declarar, no es un promedio y va como total.
 */
function Kpi({
  label,
  value,
  sample,
  hint,
  themeClasses,
  surface,
}: {
  label: string;
  value: string;
  sample?: string | null;
  hint?: string;
  themeClasses: ThemeClasses;
  surface: string;
}) {
  return (
    <div className={`${surface} rounded-xl p-3.5 min-w-0`} title={hint}>
      <p
        className={`text-[11px] uppercase tracking-wider ${themeClasses.textSecondary} font-medium`}
      >
        {label}
      </p>
      <p className={`text-lg font-bold ${themeClasses.text} mt-0.5 break-words`}>
        {value}
      </p>
      {sample && (
        <p className={`text-[11px] ${themeClasses.textSecondary} mt-0.5`}>
          {sample}
        </p>
      )}
    </div>
  );
}

/**
 * El bloque de OPERACIÓN de la modal de detalle (#501).
 *
 * La modal mostraba sólo configuración —ubicación, radio, teléfono de caseta,
 * zona horaria, geocerca, usuarios y dispositivos—: cero operación. Este bloque
 * responde las preguntas que el hito plantea sobre un patio abierto, en el rango
 * que el operador elija, y va ARRIBA de la configuración porque el orden de la
 * modal es una afirmación sobre qué se viene a ver acá.
 *
 * Todo lo decidible vive en `yardOperation.ts`, que es puro: un worktree no
 * recibe `apps/v5/.env` y cualquier componente que importe transitivamente
 * `utils/api` → firebase no se puede ni colectar bajo vitest. Este archivo es
 * presentación.
 */
export default function YardOperationPanel({
  yard,
  themeClasses,
  isDarkMode,
}: {
  yard: Yard;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
}) {
  const { t } = useTranslation("truckYard");
  const surface = isDarkMode ? "bg-[#252525]" : "bg-gray-50";

  // Abre en el día en curso DEL PATIO. Es criterio de aceptación de #501 y el
  // criterio del hito: la tarjeta de atrás muestra hoy, y un panel que abriera
  // en "últimos 30 días" daría números que no cierran con ella.
  const [preset, setPreset] = useState<YardRangePreset>("today");
  const [customFrom, setCustomFrom] = useState<string>(
    () => yardPresetRange("today", yard.timeZone).from,
  );
  const [customTo, setCustomTo] = useState<string>(
    () => yardPresetRange("today", yard.timeZone).to,
  );

  // Sin `useMemo` a propósito. `yardPresetRange` mira el reloj, así que
  // memoizarlo por [preset, timeZone] congelaría el rango en el día que estaba
  // corriendo cuando se abrió la modal: una modal dejada abierta cruzando la
  // medianoche del patio seguiría diciendo "Hoy" y pidiendo ayer. El objeto se
  // recrea en cada render, pero nada depende de su identidad — TanStack
  // serializa la clave por valor, y `yardLocalDay` cachea su Intl.DateTimeFormat
  // por zona justamente para que esto no cueste nada.
  // `null` mientras el rango personalizado esté a medio escribir: ver
  // yardCustomRange. No se consulta con un extremo vacío porque el backend lo
  // lee como "sin ese límite" y devolvería el histórico entero rotulado con el
  // rango elegido.
  const range =
    preset === "custom"
      ? yardCustomRange(customFrom, customTo)
      : yardPresetRange(preset, yard.timeZone);

  const {
    data,
    isLoading,
    isError,
    isFetching,
    retry,
  } = useYardOperationMetrics(yard, range);

  const presets: YardRangePreset[] = ["today", "last7", "last30", "custom"];

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h4
          className={`text-sm font-bold ${themeClasses.text} flex items-center gap-2`}
        >
          <i className="ri-bar-chart-2-line text-amber-500"></i>
          {t("yards.operation.panelTitle")}
        </h4>

        {/* Selector de rango. Botones y no un `select`: son cuatro opciones y en
            tablet un menú desplegable cuesta dos toques donde esto cuesta uno. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPreset(p)}
              className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                preset === p
                  ? "bg-amber-500/15 text-amber-500 border-amber-500/40 font-medium"
                  : `${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover}`
              }`}
            >
              {t(`yards.operation.range.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {preset === "custom" && (
        <div className="flex flex-wrap items-center gap-2">
          <label className={`text-[11px] ${themeClasses.textSecondary}`}>
            {t("yards.operation.range.from")}
          </label>
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className={`text-xs px-2 py-1 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text}`}
          />
          <label className={`text-[11px] ${themeClasses.textSecondary}`}>
            {t("yards.operation.range.to")}
          </label>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className={`text-xs px-2 py-1 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg} ${themeClasses.text}`}
          />
        </div>
      )}

      {/* El rango se escribe con la verdad del backend: el filtro está anclado a
          `entry_timestamp` (yardMetricsController devuelve `anchor`), así que
          una visita que entró ANTES del rango y salió adentro no está incluida.
          Sin esta línea, "últimos 7 días" se leería como "todo lo que pasó en la
          semana", que es otra cosa. */}
      <p className={`text-[11px] ${themeClasses.textSecondary}`}>
        <i className="ri-calendar-line mr-1"></i>
        {range
          ? t("yards.operation.range.anchorNote", {
              from: range.from,
              to: range.to,
            })
          : t("yards.operation.range.incomplete")}
      </p>

      {!range ? null : isLoading ? (
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {t("yards.operation.loading")}
        </p>
      ) : isError || !data ? (
        /* El error se dice y NO se rellena con nada: ni el rango anterior ni un
           cero. Lo que sí cambia respecto de la primera versión es que ahora hay
           salida sin mover el selector. Antes, con `retry: 0` y sin intervalo
           para un rango ya terminado, un corte de red de un segundo dejaba este
           cartel pegado hasta que el usuario cambiara el rango y volviera — la
           única forma de invalidar la clave, porque `from`/`to` están en ella.

           Los fallos transitorios ya se reintentan solos (shouldRetryYardMetrics)
           y ni llegan hasta acá; el botón es para el resto: el 403 del recorte
           por patio (#495) o el 404, que no se reintentan a ciegas a propósito
           pero sí pueden dejar de valer si al usuario le acaban de dar acceso.
           Ahí el gesto lo pide una persona, que es la diferencia con martillear
           al backend. */
        <div className="flex flex-wrap items-center gap-2">
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            <i className="ri-error-warning-line mr-1"></i>
            {t("yards.operation.error")}
          </p>
          <button
            type="button"
            onClick={retry}
            disabled={isFetching}
            className={`text-xs px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-60 ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
          >
            <i
              className={`mr-1 ${isFetching ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}`}
            ></i>
            {/* El texto cambia con `isFetching` porque una consulta en error que
                está reintentando sigue en `status: "error"`: sin esto, el botón
                se vería idéntico apretado y sin apretar. */}
            {isFetching
              ? t("yards.operation.retrying")
              : t("yards.operation.retry")}
          </button>
        </div>
      ) : (
        <OperationBody
          metrics={data}
          yard={yard}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          surface={surface}
        />
      )}
    </section>
  );
}

function OperationBody({
  metrics,
  yard,
  themeClasses,
  isDarkMode,
  surface,
}: {
  metrics: YardMetricsResponse;
  yard: Yard;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  surface: string;
}) {
  const { t } = useTranslation("truckYard");

  const counts = visitsToStatusCounts(metrics.visits);
  const dwell = yardDwellSample(metrics);
  const litresVisit = yardLitresVisitSample(metrics);
  const unitsUniverse = yardDistinctUnitsUniverse(metrics);
  const quality = readWaterQuality(metrics.water_quality);

  const groupBars = yardGroupBars(metrics, {
    others: t("yards.operation.charts.others"),
    noGroup: t("yards.operation.charts.noGroup"),
  });
  const unitBars = yardUnitBars(metrics, {
    others: t("yards.operation.charts.others"),
    card: (n: string) => t("yards.operation.charts.card", { card: n }),
    unidentified: t("yards.operation.charts.unidentified"),
  });

  const breakdown = metrics.water_types?.breakdown ?? [];
  // Un guion cuando ninguna visita del rango trae tipo: "Sin tipo" es la
  // ausencia del dato y no puede ser la respuesta a "cuál se usó más". Con 157
  // de 160 inspecciones de producción sin `water_type` (4-sep-2026) éste es el
  // caso normal, no el borde — el reparto completo se ve igual en la tabla.
  const topWaterType = yardTopWaterType(metrics);

  const link = yardLinkState(yard.health);
  const linkPalette = yardLinkClasses(link);
  const health = yard.health;
  // Los cuatro contadores de puertas NO son cuatro categorías paralelas:
  // `doors_down` es un subconjunto de `doors_monitored`. Ver yardDoorsBreakdown.
  const doors = yardDoorsBreakdown(health);

  const dash = "—";

  return (
    <div className="space-y-4">
      {/* ── Conteos por estado, mismo vocabulario que la tarjeta ── */}
      {metrics.visits.total === 0 ? (
        // Un cero explícito: "no hubo movimiento en este rango" y "no cargó" son
        // respuestas distintas y una fila vacía las confunde.
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          <i className="ri-inbox-line mr-1"></i>
          {t("yards.operation.noMovementRange")}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {INSPECTION_STATUS_ORDER.map(
            (status) =>
              counts[status] > 0 && (
                <InspectionStatusPill
                  key={status}
                  status={status}
                  count={counts[status]}
                  compact
                  title={
                    status === "complete"
                      ? t("yards.operation.completeHint")
                      : undefined
                  }
                />
              ),
          )}
          <span className={`text-[11px] ${themeClasses.textSecondary} ml-1`}>
            {t("yards.operation.totalVisits", { total: metrics.visits.total })}
          </span>
        </div>
      )}

      {/* ── Salud del controlador (#498) ── */}
      <div className={`${surface} rounded-xl p-3.5 flex flex-wrap items-center gap-2`}>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full font-medium text-xs px-2.5 py-1 ${linkPalette.chip}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${linkPalette.dot}`} />
          {t(`yards.health.link.${link}`)}
        </span>
        {/* Misma frase que la tarjeta, mismo componente: la modal no puede
            decir otra cosa que la tarjeta desde la que se abrió. */}
        <YardDownCauseLine health={health} className={themeClasses.text} />
        {/* El detalle "por puerta" que pide #501 se muestra con los cuatro
            contadores, y sólo cuando hay más de una: con una sola puerta el
            desglose repite lo que ya dice la píldora. Lo que NO se puede pintar
            es el estado de CADA puerta con su nombre: `computeYardHealth`
            (yardHealthService.js:401-404) embebe el agregado —total,
            monitoreadas, caídas, sin latido— y no la lista de dispositivos, así
            que nombrarlas exigiría un request más contra `hikvisionDevices`. */}
        {doors && (
          <span className={`text-[11px] ${themeClasses.textSecondary}`}>
            {doors.down === null
              ? t("yards.operation.health.doorsBreakdownNoHeartbeat", {
                  total: doors.total,
                })
              : t("yards.operation.health.doorsBreakdown", {
                  total: doors.total,
                  monitored: doors.monitored,
                  down: doors.down,
                  withoutHeartbeat: doors.withoutHeartbeat,
                })}
          </span>
        )}
        {health?.last_event_age_ms != null && (
          <span className={`text-[11px] ${themeClasses.textSecondary}`}>
            <i className="ri-time-line mr-1"></i>
            {t("yards.health.lastPass", {
              age: formatAge(health.last_event_age_ms),
            })}
          </span>
        )}
      </div>

      {/* ── Los números ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Kpi
          themeClasses={themeClasses}
          surface={surface}
          label={t("yards.operation.kpi.topWaterType")}
          value={topWaterType ? topWaterType.label : dash}
          // El guion sin denominador se leería como "no cargó". Con el `n` en
          // cero se lee como lo que es: nadie capturó el tipo de agua, que es lo
          // que pasa en 157 de 160 inspecciones de producción.
          sample={
            topWaterType
              ? t("yards.operation.sample.waterTypeShare", {
                  n: topWaterType.n,
                  universe: metrics.water_types?.n ?? 0,
                })
              : t("yards.operation.sample.noWaterType", {
                  universe: metrics.water_types?.n ?? 0,
                })
          }
        />
        <Kpi
          themeClasses={themeClasses}
          surface={surface}
          label={t("yards.operation.kpi.litresTotal")}
          // `yardAggregateSum` y no `.sum` directo: el servidor manda
          // `{sum: 0, n: 0}` cuando NADIE capturó un volumen —sólo `avg` nace
          // null— y un 0 acá no dice "no hay dato", dice "midieron y no salió
          // nada". Con 1 de 160 inspecciones de producción con volumen capturado
          // (4-sep-2026), ese 0 es lo que se veía casi siempre.
          value={formatLitres(yardAggregateSum(metrics.litres?.per_visit)) ?? dash}
          sample={t("yards.operation.sample.litresVisit", {
            n: litresVisit.n,
            universe: litresVisit.universe,
          })}
          hint={t("yards.operation.sample.litresDropped", {
            unparseable: litresVisit.unparseable,
            missing: litresVisit.missing,
            cancelled: litresVisit.excludedCancelled,
          })}
        />
        {/* "Litros promedio por pipa" se lee de dos maneras y por eso van los
            dos, cada uno con su nombre: una unidad que entró tres veces aporta
            tres visitas y una sola unidad. El servidor los devuelve con el mismo
            `sum` y distinto denominador (litres.per_visit / litres.per_vehicle),
            justamente para que la etiqueta no pueda mentir. */}
        <Kpi
          themeClasses={themeClasses}
          surface={surface}
          label={t("yards.operation.kpi.litresPerVisit")}
          value={formatLitres(metrics.litres?.per_visit?.avg) ?? dash}
          sample={t("yards.operation.sample.litresVisit", {
            n: litresVisit.n,
            universe: litresVisit.universe,
          })}
          hint={t("yards.operation.sample.litresDropped", {
            unparseable: litresVisit.unparseable,
            missing: litresVisit.missing,
            cancelled: litresVisit.excludedCancelled,
          })}
        />
        <Kpi
          themeClasses={themeClasses}
          surface={surface}
          label={t("yards.operation.kpi.litresPerUnit")}
          value={formatLitres(metrics.litres?.per_vehicle?.avg) ?? dash}
          // El universo de unidades distintas sólo se conoce cuando el top llegó
          // corto (ver yardDistinctUnitsUniverse). Cuando no, se declara el `n`
          // sin denominador en vez de inventar uno.
          sample={
            unitsUniverse !== null
              ? t("yards.operation.sample.litresUnit", {
                  n: metrics.litres?.per_vehicle?.n ?? 0,
                  universe: unitsUniverse,
                })
              : t("yards.operation.sample.litresUnitNoUniverse", {
                  n: metrics.litres?.per_vehicle?.n ?? 0,
                })
          }
        />
        <Kpi
          themeClasses={themeClasses}
          surface={surface}
          label={t("yards.operation.kpi.dwell")}
          value={formatDwell(dwell.seconds) ?? dash}
          // El ejemplo textual de #501: "43 min (n = 28 de 51 visitas cerradas)".
          // 11 de las 20 tags de Acuapue nunca fueron leídas por la lectora de
          // salida, así que sin esta línea el promedio afirma algo que no midió.
          sample={`${t("yards.operation.sample.dwell", {
            n: dwell.n,
            closed: dwell.closed,
          })}${
            dwell.withoutExit > 0
              ? ` · ${t("yards.operation.sample.dwellExcluded", {
                  withoutExit: dwell.withoutExit,
                })}`
              : ""
          }`}
          hint={t("yards.operation.sample.dwellHint", {
            unmeasurable: dwell.unmeasurable,
            negative: dwell.droppedNegative,
          })}
        />
      </div>

      {/* ── Litros por tipo de agua ── */}
      {breakdown.length > 0 && (
        <div className={`${surface} rounded-xl p-3.5`}>
          <p
            className={`text-[11px] uppercase tracking-wider ${themeClasses.textSecondary} font-medium mb-2`}
          >
            {t("yards.operation.waterTypes.title")}
          </p>
          {/* La tabla scrollea dentro de su caja y no empuja la modal: en tablet
              vertical el ancho útil de la modal es de ~700 px y cuatro columnas
              numéricas con etiquetas en español no entran sin apretar. */}
          <div className="overflow-x-auto -mx-1 px-1">
            <table className="w-full text-xs min-w-[420px]">
              <thead>
                <tr className={themeClasses.textSecondary}>
                  <th className="text-left font-medium py-1">
                    {t("yards.operation.waterTypes.type")}
                  </th>
                  <th className="text-right font-medium py-1">
                    {t("yards.operation.waterTypes.visits")}
                  </th>
                  <th className="text-right font-medium py-1">
                    {t("yards.operation.waterTypes.litresTotal")}
                  </th>
                  <th className="text-right font-medium py-1">
                    {t("yards.operation.waterTypes.litresAvg")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((b) => (
                  <tr
                    key={b.water_type ?? "__none__"}
                    className={`border-t ${themeClasses.cardBorder}`}
                  >
                    <td className={`py-1.5 ${themeClasses.text}`}>
                      {b.label}
                      {/* Un valor fuera del catálogo de tres
                          (components/waterTypes.ts) no se esconde: si aparece,
                          es un dato que alguien escribió y hay que verlo. */}
                      {!b.known && b.water_type !== null && (
                        <span className="ml-1 text-[10px] text-amber-500">
                          {t("yards.operation.waterTypes.unknown")}
                        </span>
                      )}
                    </td>
                    <td className={`py-1.5 text-right ${themeClasses.text}`}>
                      {b.n}
                    </td>
                    <td className={`py-1.5 text-right ${themeClasses.text}`}>
                      {/* Mismo motivo que el KPI: con `n = 0` el servidor manda
                          `sum: 0` y la fila salía "Total 0 · Promedio —", que se
                          contradice sola. */}
                      {formatLitres(yardAggregateSum(b.litres)) ?? dash}
                    </td>
                    <td className={`py-1.5 text-right ${themeClasses.text}`}>
                      {formatLitres(b.litres?.avg) ?? dash}
                      <span className={`ml-1 ${themeClasses.textSecondary}`}>
                        {t("yards.operation.waterTypes.avgSample", {
                          n: b.litres?.n ?? 0,
                          universe: b.n,
                        })}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── pH, conductividad y turbidez ──
          El bloque se pinta AUNQUE los tres estén en cero, y ésa es la decisión
          que importa acá: medido el 4-sep-2026, ninguna de las 160 inspecciones
          de producción ni de las 130 de dev tiene los tres campos —la captura de
          #502 se desplegó ese mismo día—, así que "0 de 161 visitas" es lo único
          que le avisa a operación que el formulario nuevo todavía no se está
          usando. Escondiéndolo, "nadie capturó una lectura" y "esta función no
          existe" se ven igual.

          Sólo desaparece contra un backend que devuelve `water_quality: null`,
          que es el que no tiene el agregado. */}
      {quality && (
        <div className={`${surface} rounded-xl p-3.5 space-y-3`}>
          <p
            className={`text-[11px] uppercase tracking-wider ${themeClasses.textSecondary} font-medium`}
          >
            {t("yards.operation.quality.title")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {quality.parameters.map((p) => (
              <div key={p.key}>
                <p className={`text-[11px] ${themeClasses.textSecondary}`}>
                  {/* La etiqueta la manda el servidor y la traducción del
                      cliente sólo la pisa si existe: un parámetro nuevo aparece
                      con su nombre en vez de con la clave cruda. */}
                  {t(`yards.operation.quality.param.${p.key}`, {
                    defaultValue: p.label,
                  })}
                  {p.unit ? ` (${p.unit})` : ""}
                </p>
                {/* `avg: null` con `n: 0` es la ausencia de muestras, no un cero
                    medido. Un "pH 0" sería un dato falso y además alarmante. */}
                <p className={`text-lg font-bold ${themeClasses.text}`}>
                  {p.avg != null ? p.avg.toFixed(2) : dash}
                </p>
                <p className={`text-[11px] ${themeClasses.textSecondary}`}>
                  {t("yards.operation.quality.sample", {
                    n: p.n,
                    universe: quality.n,
                  })}
                </p>
              </div>
            ))}
          </div>

          {quality.n > 0 && quality.parameters.every((p) => p.n === 0) && (
            <p className="text-[11px] text-amber-500">
              <i className="ri-error-warning-line mr-1"></i>
              {t("yards.operation.quality.noneCaptured")}
            </p>
          )}

          {/* El corte por tipo de agua que pide #501. Viaja dentro del mismo
              `breakdown[]` que los litros, con el mismo denominador del cubo, así
              que la fila declara su `n` sin cruzar datos de dos lugares. */}
          {breakdown.length > 0 && (
            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-xs min-w-[420px]">
                <thead>
                  <tr className={themeClasses.textSecondary}>
                    <th className="text-left font-medium py-1">
                      {t("yards.operation.waterTypes.type")}
                    </th>
                    {quality.parameters.map((p) => (
                      <th key={p.key} className="text-right font-medium py-1">
                        {t(`yards.operation.quality.param.${p.key}`, {
                          defaultValue: p.label,
                        })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {breakdown.map((b) => (
                    <tr
                      key={b.water_type ?? "__none__"}
                      className={`border-t ${themeClasses.cardBorder}`}
                    >
                      <td className={`py-1.5 ${themeClasses.text}`}>{b.label}</td>
                      {quality.parameters.map((p) => {
                        const agg = waterTypeQuality(b, p.key);
                        return (
                          <td
                            key={p.key}
                            className={`py-1.5 text-right ${themeClasses.text}`}
                          >
                            {agg?.avg != null ? agg.avg.toFixed(2) : dash}
                            <span className={`ml-1 ${themeClasses.textSecondary}`}>
                              {t("yards.operation.waterTypes.avgSample", {
                                n: agg?.n ?? 0,
                                universe: b.n,
                              })}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Las dos gráficas ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className={`${surface} rounded-xl p-3.5 min-w-0`}>
          <p
            className={`text-[11px] uppercase tracking-wider ${themeClasses.textSecondary} font-medium mb-2`}
          >
            {t("yards.operation.charts.groups")}
          </p>
          {groupBars.length === 0 ? (
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {t("yards.operation.charts.empty")}
            </p>
          ) : (
            <>
              <HorizontalBars
                data={groupBars}
                isDarkMode={isDarkMode}
                seriesName={t("yards.operation.charts.visits")}
              />
              <ChartUniverse bars={groupBars} themeClasses={themeClasses} />
            </>
          )}
        </div>

        <div className={`${surface} rounded-xl p-3.5 min-w-0`}>
          <p
            className={`text-[11px] uppercase tracking-wider ${themeClasses.textSecondary} font-medium mb-2`}
          >
            {t("yards.operation.charts.units")}
          </p>
          {unitBars.length === 0 ? (
            <p className={`text-xs ${themeClasses.textSecondary}`}>
              {t("yards.operation.charts.empty")}
            </p>
          ) : (
            <>
              <HorizontalBars
                data={unitBars}
                isDarkMode={isDarkMode}
                seriesName={t("yards.operation.charts.visits")}
              />
              <ChartUniverse bars={unitBars} themeClasses={themeClasses} />
              {/* La leyenda sólo aparece cuando hay algo que explicar. Una barra
                  ámbar es una unidad que llegó al respaldo del número de tarjeta
                  —sin `vehicle_id` ni `vehicle_plate`—, o sea una visita
                  `missing_vehicle`: la que más importa vigilar y la que se
                  perdería agrupando sólo por `vehicle_id`. */}
              {unitBars.some((b) => b.kind === "missing_vehicle") && (
                <p className={`text-[11px] text-amber-500 mt-1`}>
                  <i className="ri-error-warning-line mr-1"></i>
                  {t("yards.operation.charts.missingVehicleHint")}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Avisos de recorte ──
          Los dos son techos de lectura del servidor, no del negocio, y cuando se
          tocan los números de arriba dejan de ser el total. Producción tiene 149
          inspecciones en TOTAL medidas el 3-sep-2026 contra un techo de 5.000,
          así que hoy esto no debería aparecer nunca; si aparece, es la señal de
          que hay que mirar `scan.truncated`, no de que el patio creció. */}
      {metrics.scan?.truncated && (
        <p className="text-[11px] text-amber-500">
          <i className="ri-error-warning-line mr-1"></i>
          {t("yards.operation.notes.truncated", {
            read: metrics.scan.inspections_read,
            max: metrics.scan.max_inspections,
          })}
        </p>
      )}
      {metrics.groups_resolution?.truncated && (
        <p className="text-[11px] text-amber-500">
          <i className="ri-error-warning-line mr-1"></i>
          {t("yards.operation.notes.groupsTruncated")}
        </p>
      )}
    </div>
  );
}
